import { getFieldTypeWidth, STANDARD_COLUMN_WIDTHS } from '../column-widths'
import { DEFAULT_SUPPLIER_LIFECYCLE_STAGES } from '../crm-stage'
import { FieldTypeEnum } from '../custom-field.types'
import { ModuleColumnDefinition, ModuleTypeEnum } from '../module'
import { Supplier } from '../supplier'
import { createAttachmentCountColumns } from './attachment-count-columns'

/**
 * Creates standard column definitions for the Supplier module
 */
export function createSupplierStandardColumns(
  terminology: { singular: string; plural: string } = {
    singular: 'vendor',
    plural: 'vendors',
  },
): ModuleColumnDefinition[] {
  const singular = terminology.singular
  const Singular = singular.charAt(0).toUpperCase() + singular.slice(1)

  return [
    {
      id: 'name',
      name: 'name' as keyof Supplier, // Type-safe key reference
      label: `${Singular} Name`,
      description: `Name of the ${singular}`,
      isVisible: true,
      isRequired: true,
      isSystem: true, // Cannot be disabled
      isFixed: true, // This column cannot be moved
      order: 0,
      fieldType: FieldTypeEnum.enum.STRING,
      // Primary name column: same default as other modules' name columns, so
      // the header and typical names fit with the row checkbox on one line.
      ...STANDARD_COLUMN_WIDTHS.VERY_WIDE,
    },
    {
      id: 'code',
      name: 'code' as keyof Supplier, // Type-safe key reference
      label: 'Code',
      description: `${Singular} code or identifier`,
      isVisible: true,
      isRequired: true,
      isSystem: false,
      isFixed: false,
      order: 1,
      fieldType: FieldTypeEnum.enum.STRING,
      width: '120px',
      minWidth: '120px',
    },
    {
      id: 'email',
      name: 'email' as keyof Supplier, // Type-safe key reference
      label: 'Email',
      description: `Email address of the ${singular}`,
      isVisible: true,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 2,
      fieldType: FieldTypeEnum.enum.STRING,
      width: '180px',
      minWidth: '180px',
    },
    {
      id: 'phone',
      name: 'phone' as keyof Supplier, // Type-safe key reference
      label: 'Phone',
      description: `Phone number of the ${singular}`,
      isVisible: true,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 3,
      fieldType: FieldTypeEnum.enum.STRING,
      width: '150px',
      minWidth: '150px',
    },
    {
      id: 'contactPerson',
      name: 'contactPerson' as keyof Supplier, // Type-safe key reference
      label: 'Contact Person',
      description: `Primary contact person at the ${singular}`,
      isVisible: true,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 4,
      fieldType: FieldTypeEnum.enum.STRING,
      width: '160px',
      minWidth: '160px',
    },
    {
      id: 'lifecycleStage',
      name: 'lifecycleStage' as keyof Supplier,
      label: 'Lifecycle Stage',
      description: `Vendor lifecycle stage of the ${singular} (potential / active / preferred). Org-customizable.`,
      isVisible: true,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 4.5,
      fieldType: FieldTypeEnum.enum.DROPDOWN,
      // Defaults; actual options are fetched from CrmStage (moduleType=SUPPLIER_LIFECYCLE)
      options: DEFAULT_SUPPLIER_LIFECYCLE_STAGES.filter(s => !s.isHidden).map(
        stage => ({
          value: stage.id,
          label: stage.name,
          color: stage.color,
        }),
      ),
      width: '150px',
      minWidth: '120px',
    },
    {
      id: 'isActive',
      name: 'isActive' as keyof Supplier, // Type-safe key reference
      label: 'Status',
      description: `Current status of the ${singular}`,
      isVisible: false,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 5,
      fieldType: FieldTypeEnum.enum.BOOLEAN,
      width: '100px',
      minWidth: '100px',
    },
    {
      id: 'isPreferred',
      name: 'isPreferred' as keyof Supplier, // Type-safe key reference
      label: 'Preferred',
      description: `Whether this is a preferred ${singular}`,
      isVisible: true,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 6,
      fieldType: FieldTypeEnum.enum.BOOLEAN,
      width: '120px',
      minWidth: '120px',
    },
    {
      id: 'onTimeDelivery',
      name: 'onTimeDelivery' as keyof Supplier, // Type-safe key reference
      label: 'On-Time %',
      description: 'On-time delivery percentage',
      isVisible: true,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 7,
      // Stored 0-100; render as a percentage, not a bare number.
      fieldType: FieldTypeEnum.enum.PERCENTAGE,
      width: '120px',
      minWidth: '120px',
    },
    {
      id: 'qualityScore',
      name: 'qualityScore' as keyof Supplier, // Type-safe key reference
      label: 'Quality Score',
      description: 'Quality score out of 5',
      isVisible: true,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 8,
      fieldType: FieldTypeEnum.enum.NUMBER,
      width: '120px',
      minWidth: '120px',
    },
    {
      id: 'totalPurchases',
      name: 'totalPurchases' as keyof Supplier, // Type-safe key reference
      label: 'Total Spent',
      description: `Total amount spent with this ${singular}`,
      isVisible: true,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 9,
      fieldType: FieldTypeEnum.enum.NUMBER,
      width: '140px',
      minWidth: '140px',
    },
    {
      id: 'totalOrders',
      name: 'totalOrders' as keyof Supplier, // Type-safe key reference
      label: 'Orders',
      description: 'Total number of orders',
      isVisible: true,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 10,
      fieldType: FieldTypeEnum.enum.NUMBER,
      width: '100px',
      minWidth: '100px',
    },
    {
      id: 'currency',
      name: 'currency' as keyof Supplier, // Type-safe key reference
      label: 'Currency',
      description: 'Preferred currency for transactions',
      isVisible: false,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 11,
      fieldType: FieldTypeEnum.enum.STRING,
      width: '100px',
      minWidth: '100px',
    },
    {
      id: 'paymentTerms',
      name: 'paymentTerms' as keyof Supplier, // Type-safe key reference
      label: 'Payment Terms',
      description: 'Payment terms in days',
      isVisible: false,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 12,
      fieldType: FieldTypeEnum.enum.NUMBER,
      width: '120px',
      minWidth: '120px',
    },
    {
      id: 'website',
      name: 'website' as keyof Supplier, // Type-safe key reference
      label: 'Website',
      description: `${Singular} website URL`,
      isVisible: false,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 13,
      fieldType: FieldTypeEnum.enum.STRING,
      width: '180px',
      minWidth: '180px',
    },
    {
      id: 'address',
      name: 'address' as keyof Supplier, // Type-safe key reference
      label: 'Address',
      description: `${Singular} address`,
      isVisible: true,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 14,
      fieldType: FieldTypeEnum.enum.TEXT,
      width: '200px',
      minWidth: '200px',
    },
    {
      id: 'taxId',
      name: 'taxId' as keyof Supplier, // Type-safe key reference
      label: 'Tax ID',
      description: `Tax identification number of the ${singular}`,
      isVisible: false,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 14.5,
      fieldType: FieldTypeEnum.enum.STRING,
      width: '140px',
      minWidth: '140px',
    },
    {
      id: 'notes',
      name: 'notes' as keyof Supplier, // Type-safe key reference
      label: 'Notes',
      description: `Internal notes about the ${singular}`,
      isVisible: false,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 14.6,
      fieldType: FieldTypeEnum.enum.TEXT,
      width: '200px',
      minWidth: '200px',
    },
    {
      id: 'createdAt',
      name: 'createdAt' as keyof Supplier, // Type-safe key reference
      label: 'Created Date',
      description: `Date when ${singular} was created`,
      isVisible: false,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      order: 15,
      fieldType: FieldTypeEnum.enum.DATETIME,
      width: '150px',
      minWidth: '150px',
    },
    ...createAttachmentCountColumns(singular, 16),
  ]
}

/**
 * Default standard column definitions for the Supplier module
 */
export const SUPPLIER_STANDARD_COLUMNS: ModuleColumnDefinition[] =
  createSupplierStandardColumns()

/**
 * Helper function to get all available columns for the supplier module,
 * combining standard and custom fields
 */
export function getSupplierColumns(
  customFields: any[] = [],
  visibilityPreferences?: Record<string, boolean>,
): ModuleColumnDefinition[] {
  // Start with standard columns
  const standardColumns = createSupplierStandardColumns().map(column => ({
    ...column,
    // Override visibility if preferences exist
    isVisible:
      visibilityPreferences?.[column.id] !== undefined
        ? !!visibilityPreferences[column.id] // Ensure boolean type with !!
        : column.isVisible,
  }))

  // Default width for custom field columns based on field type
  const getCustomFieldWidth = (
    fieldType: string,
  ): { width: string; minWidth: string } => {
    switch (fieldType) {
      case FieldTypeEnum.enum.STRING:
        return { width: '180px', minWidth: '180px' }
      case FieldTypeEnum.enum.TEXT:
        return { width: '200px', minWidth: '200px' }
      case FieldTypeEnum.enum.NUMBER:
        return { width: '120px', minWidth: '120px' }
      case FieldTypeEnum.enum.BOOLEAN:
        return { width: '120px', minWidth: '120px' }
      case FieldTypeEnum.enum.DATE:
      case FieldTypeEnum.enum.DATETIME:
        return { width: '150px', minWidth: '150px' }
      case FieldTypeEnum.enum.DROPDOWN:
        return { width: '150px', minWidth: '150px' }
      case FieldTypeEnum.enum.FILE:
        return { width: '200px', minWidth: '150px' }
      default:
        return { width: '150px', minWidth: '150px' }
    }
  }

  // Map custom fields to column definitions
  const customColumns = customFields.map((field, index) => {
    const widthSettings = getCustomFieldWidth(field.fieldType)

    return {
      id: `custom_${field.fieldName}`,
      name: field.fieldName,
      label: field.label,
      description: `Custom field: ${field.label}`,
      isVisible:
        visibilityPreferences?.[`custom_${field.fieldName}`] !== undefined
          ? !!visibilityPreferences[`custom_${field.fieldName}`] // Ensure boolean type with !!
          : !!field.isActive,
      isRequired: false,
      isSystem: false,
      order: 91 + index, // Custom fields come after tags (order 90)
      fieldType: field.fieldType,
      width: widthSettings.width,
      minWidth: widthSettings.minWidth,
    }
  }) as ModuleColumnDefinition[]

  // Combine both types of columns
  return [...standardColumns, ...customColumns]
}

/**
 * Get module type for column definitions
 */
export function getSupplierModuleType(): ModuleTypeEnum {
  return ModuleTypeEnum.SUPPLIER
}
